import { PantryItem, PantryImportBatch } from '../types/pantry';

const API_BASE_URL = process.env.REACT_APP_API_URL;

const getAuthHeaders = (token: string) => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${token}`,
});

const getMultipartAuthHeaders = (token: string) => ({
  Authorization: `Bearer ${token}`,
});

/**
 * Upload images and start pantry import process
 */
export const importPantryFromImages = async (
  token: string,
  images: File[]
): Promise<{
  message: string;
  status: string;
  importId: string;
}> => {
  const formData = new FormData();
  images.forEach((image) => {
    formData.append('images', image);
  });

  const response = await fetch(`${API_BASE_URL}/api/pantry/import/images`, {
    method: 'POST',
    headers: getMultipartAuthHeaders(token),
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to start pantry import: ${response.status}`);
  }

  return response.json();
};

/**
 * Upload voice recording and start pantry import process
 */
export const importPantryFromVoice = async (
  token: string,
  audio: Blob
): Promise<{
  message: string;
  status: string;
  importId: string;
}> => {
  const formData = new FormData();
  formData.append('audio', audio, 'recording.webm');

  const response = await fetch(`${API_BASE_URL}/api/pantry/import/voice`, {
    method: 'POST',
    headers: getMultipartAuthHeaders(token),
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to start pantry voice import: ${response.status}`);
  }

  return response.json();
};

/**
 * Upload video and start pantry import process (legacy direct upload).
 * Prefer importPantryFromUploadedVideo for files >32MB to avoid Cloud Run limits.
 */
export const importPantryFromVideo = async (
  token: string,
  video: Blob
): Promise<{
  message: string;
  status: string;
  importId: string;
}> => {
  const formData = new FormData();
  formData.append('video', video, 'recording.webm');

  const response = await fetch(`${API_BASE_URL}/api/pantry/import/video`, {
    method: 'POST',
    headers: getMultipartAuthHeaders(token),
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to start pantry video import: ${response.status}`);
  }

  return response.json();
};

/**
 * Start pantry import from a video already uploaded to GCS via signed URL.
 * This bypasses the 32MB Cloud Run request body limit.
 */
export const importPantryFromUploadedVideo = async (
  token: string,
  fileId: string
): Promise<{
  message: string;
  status: string;
  importId: string;
}> => {
  const response = await fetch(`${API_BASE_URL}/api/pantry/import/video/from-upload`, {
    method: 'POST',
    headers: getAuthHeaders(token),
    body: JSON.stringify({ fileId }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to start pantry video import: ${response.status}`);
  }

  return response.json();
};

/**
 * Get pantry import status by ID
 */
export const getPantryImportStatus = async (
  token: string,
  importId: string
): Promise<{ importBatch: PantryImportBatch }> => {
  const response = await fetch(`${API_BASE_URL}/api/pantry/import/${importId}`, {
    method: 'GET',
    headers: getAuthHeaders(token),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to get import status: ${response.status}`);
  }

  return response.json();
};

/**
 * Get pantry items that need review
 */
export const getPantryItemsNeedingReview = async (
  token: string
): Promise<{ items: PantryItem[] }> => {
  const response = await fetch(`${API_BASE_URL}/api/pantry/review`, {
    method: 'GET',
    headers: getAuthHeaders(token),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to get review items: ${response.status}`);
  }

  return response.json();
};

/**
 * Approve a pantry item (remove needsReview flag)
 */
export const approvePantryItem = async (
  token: string,
  itemId: string,
  updates?: Partial<PantryItem>
): Promise<{
  success: boolean;
  item: PantryItem;
  message: string;
}> => {
  const response = await fetch(`${API_BASE_URL}/api/pantry/${itemId}/approve`, {
    method: 'POST',
    headers: getAuthHeaders(token),
    body: JSON.stringify(updates || {}),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to approve pantry item: ${response.status}`);
  }

  return response.json();
};

/**
 * Get all pantry items
 */
export const getPantryItems = async (
  token: string
): Promise<{ success: boolean; items: PantryItem[] }> => {
  const response = await fetch(`${API_BASE_URL}/api/pantry`, {
    method: 'GET',
    headers: getAuthHeaders(token),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to get pantry items: ${response.status}`);
  }

  return response.json();
};

/**
 * Create a new pantry item
 */
export const createPantryItem = async (
  token: string,
  itemData: Partial<PantryItem>
): Promise<{ success: boolean; item: PantryItem; message: string }> => {
  const response = await fetch(`${API_BASE_URL}/api/pantry`, {
    method: 'POST',
    headers: getAuthHeaders(token),
    body: JSON.stringify(itemData),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to create pantry item: ${response.status}`);
  }

  return response.json();
};

/**
 * Update an existing pantry item
 */
export const updatePantryItem = async (
  token: string,
  itemId: string,
  updates: Partial<PantryItem>
): Promise<{ success: boolean; item: PantryItem; message: string }> => {
  const response = await fetch(`${API_BASE_URL}/api/pantry/${itemId}`, {
    method: 'PUT',
    headers: getAuthHeaders(token),
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to update pantry item: ${response.status}`);
  }

  return response.json();
};

/**
 * Delete a pantry item
 */
export const deletePantryItem = async (
  token: string,
  itemId: string
): Promise<{ success: boolean; message: string }> => {
  const response = await fetch(`${API_BASE_URL}/api/pantry/${itemId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(token),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to delete pantry item: ${response.status}`);
  }

  return response.json();
};

/**
 * Undo deletion of a pantry item
 */
export const undoDeletePantryItem = async (
  token: string,
  itemId: string
): Promise<{ success: boolean; message: string }> => {
  const response = await fetch(`${API_BASE_URL}/api/pantry/${itemId}/undo`, {
    method: 'POST',
    headers: getAuthHeaders(token),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to undo pantry item deletion: ${response.status}`);
  }

  return response.json();
};

/**
 * Bulk approve all (or specified) review items
 */
export const approveAllPantryItems = async (
  token: string,
  itemIds?: string[]
): Promise<{ success: boolean; approvedCount: number; message: string }> => {
  const response = await fetch(`${API_BASE_URL}/api/pantry/review/approve-all`, {
    method: 'POST',
    headers: getAuthHeaders(token),
    body: JSON.stringify(itemIds ? { itemIds } : {}),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to approve all items: ${response.status}`);
  }

  return response.json();
};

/**
 * Bulk reject (soft-delete) all or specified review items
 */
export const rejectAllPantryItems = async (
  token: string,
  itemIds?: string[]
): Promise<{ success: boolean; rejectedCount: number; message: string }> => {
  const response = await fetch(`${API_BASE_URL}/api/pantry/review/reject-all`, {
    method: 'POST',
    headers: getAuthHeaders(token),
    body: JSON.stringify(itemIds ? { itemIds } : {}),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to reject all items: ${response.status}`);
  }

  return response.json();
};